/**
 * Tauri desktop bridge.
 * Detects whether we're running inside the Arkphone desktop wrapper
 * (Tauri 2) and, if so, exposes native commands. Falls back to no-op /
 * web behaviour in a plain browser tab.
 */

type BrowserChoice = 'chrome' | 'firefox' | 'edge' | 'default' | string;

const w = typeof window !== 'undefined' ? (window as any) : null;
// Tauri 2 exposes __TAURI_INTERNALS__ (not __TAURI__ like v1). Also accept the v1 flag
// in case a future bridge re-introduces it.
export const isDesktop =
  !!(w && (w.__TAURI_INTERNALS__ || w.__TAURI__ || w.isTauri));

if (typeof window !== 'undefined') {
  console.log('[desktop] isDesktop =', isDesktop, {
    hasInternals: !!w?.__TAURI_INTERNALS__,
    hasTauri: !!w?.__TAURI__,
    hasFlag: !!w?.isTauri,
    ua: navigator.userAgent.slice(0, 80),
  });
}

async function invoke<T = unknown>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  // Don't short-circuit on isDesktop — let the actual invoke decide.
  // In a plain browser, the dynamic import succeeds but the invoke call throws.
  const { invoke: tauriInvoke } = await import('@tauri-apps/api/core');
  return tauriInvoke<T>(cmd, args);
}

/**
 * Launch a URL in a specific browser binary (desktop only).
 * Throws in a plain browser — caller is expected to catch and fall back.
 */
export async function launchBrowser(url: string, browser: BrowserChoice = 'default'): Promise<void> {
  await invoke('launch_browser', { opts: { browser, url } });
}

/**
 * Native OS notification (Windows toast / macOS banner).
 * Requires permission on first call. Returns true if shown, false otherwise.
 */
export async function showNativeNotification(title: string, body: string): Promise<boolean> {
  if (!isDesktop) return false;
  try {
    const notification = await import('@tauri-apps/plugin-notification');
    let granted = await notification.isPermissionGranted();
    if (!granted) {
      const res = await notification.requestPermission();
      granted = res === 'granted';
    }
    if (!granted) return false;
    await notification.sendNotification({ title, body });
    return true;
  } catch {
    return false;
  }
}

/** Set tray icon state — 'ready' | 'ringing' | 'in_call' | 'offline'. */
export async function setTrayStatus(status: string): Promise<void> {
  if (!isDesktop) return;
  try { await invoke('set_tray_status', { status }); } catch { /* */ }
}

/**
 * Resize the Tauri window programmatically. Used to keep the login screen
 * compact (540×720) and expand to a full workspace size (1320×820) once the
 * agent is signed in. No-op in the web build.
 */
export async function setWindowSize(width: number, height: number, opts: { center?: boolean } = {}): Promise<void> {
  if (!isDesktop) return;
  try {
    const { getCurrentWindow, LogicalSize } = await import('@tauri-apps/api/window');
    const win = getCurrentWindow();
    await win.setSize(new LogicalSize(width, height));
    if (opts.center !== false) await win.center();
  } catch (e) {
    console.warn('[desktop] setWindowSize failed', e);
  }
}

// ─── OS Credential Store ────────────────────────────────────────────────
//
// Long-lived secrets (refresh token, SIP secret) belong in the OS credential
// manager, not localStorage. Caller is expected to gate on `isDesktop`:
// in a plain browser these fall through to localStorage.

const LS_PREFIX = 'ccark.cred.';

export async function credSet(key: string, value: string): Promise<void> {
  if (isDesktop) {
    await invoke('cred_set', { key, value });
    return;
  }
  localStorage.setItem(LS_PREFIX + key, value);
}

export async function credGet(key: string): Promise<string | null> {
  if (isDesktop) {
    try { return (await invoke<string | null>('cred_get', { key })) ?? null; }
    catch { return null; }
  }
  return localStorage.getItem(LS_PREFIX + key);
}

export async function credClear(key: string): Promise<void> {
  if (isDesktop) {
    try { await invoke('cred_clear', { key }); } catch { /* */ }
    return;
  }
  localStorage.removeItem(LS_PREFIX + key);
}
